import { useEffect, useState } from 'react';

import TaskService from 'src/services/tasks/taskService';
import { type FeatureFlags } from 'src/store/userSlice';
import { isUpperOrWholeBodyTask, WeightRestrictionMaxRangeHandler } from 'src/utils/restrictionUtilsLegacy';

import { type ValidationTaskGroup } from './types';

interface MaxValueConfigurable {
  restrictionMaxValue?: undefined | number | null;
}
const childTaskSortComparator = (a: MaxValueConfigurable, b: MaxValueConfigurable) =>
  (a.restrictionMaxValue || 0) - (b.restrictionMaxValue || 0);

/** This hook is meant to encapsulate the complexity of supporting validation of restrictions with regardless of whether
 * task grouping is enabled or disabled. The actual validation is in a separate utility method for cleaner dependency
 * management and type checking */
export const useFetchTaskGroups = (
  isFeatureFlagsLoaded: boolean,
  featureFlags: FeatureFlags,
  regionId: number | undefined,
): { isLoading: boolean; taskGroups: ValidationTaskGroup[] } => {
  const [taskGroups, setTaskGroups] = useState<ValidationTaskGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isFeatureFlagsLoaded || regionId == undefined || regionId < 1) return;
    (async () => {
      setIsLoading(true);
      try {
        if (featureFlags.TASK_GROUPING) {
          const { taskGroups } = await TaskService.fetchRegionGroupsAndTasksByRegionId(regionId);
          taskGroups.forEach((tg) => tg.tasks.sort(childTaskSortComparator));
          setTaskGroups(taskGroups);
        } else {
          const regionTasks = await TaskService.fetchRegionTasksByRegionId(regionId);
          if (!regionTasks.length) {
            // handle edge case as well as unit tests
            setTaskGroups([]);
            return;
          }

          // Get list of grouped tasks
          const liftCarryTasks = regionTasks.filter((t) => t.label.startsWith('Lift/Carry'));
          const pushPullTasks = regionTasks.filter(
            (t) => t.label.startsWith('Push/Pull') && !isUpperOrWholeBodyTask(t),
          );

          // Parse restrictionMaxValue
          liftCarryTasks.forEach((t) => {
            t.restrictionMaxValue =
              t.restrictionMaxValue ?? WeightRestrictionMaxRangeHandler.getChildOption(t.label).value;
          });
          pushPullTasks.forEach((t) => {
            t.restrictionMaxValue =
              t.restrictionMaxValue ?? WeightRestrictionMaxRangeHandler.getChildOption(t.label).value;
          });

          // Sort
          liftCarryTasks.sort(childTaskSortComparator);
          pushPullTasks.sort(childTaskSortComparator);

          const liftCarryTaskGroup: ValidationTaskGroup = {
            tasks: liftCarryTasks,
          };
          const pushPullTaskGroup: ValidationTaskGroup = {
            tasks: pushPullTasks,
          };
          setTaskGroups([liftCarryTaskGroup, pushPullTaskGroup]);
        }
      } finally {
        // fail gracefully, allow menus to function as if there were no validation
        setIsLoading(false);
      }
    })();
  }, [isFeatureFlagsLoaded, featureFlags, regionId]);

  return { isLoading, taskGroups };
};
